import { v4 as uuid } from 'uuid';

import { SBaseDisplay, SDate, isString } from '@summit/core';
import {
  DaysOfWeek,
  DaysOfWeekToString,
  MileageFormatter,
  MoneyFormatter,
  NumberFormatter,
  PercentageFormatter,
  RateFormatter,
  SummitFormattedText
} from '@summit/models';

import { EboObjectTimePeriod } from '../../base-classes/ebo-object-time-period';
import { Employee } from '../../employee/employee';
import { EmployeePayrollGroup } from '../../employee/employee-payroll-group';
import { Company } from '../../organization/company/company';
import { PayUnit } from '../../payroll/pay-unit.enum';
import { PayType, PayTypeMapping } from '../../payroll/shared/pay-type.enum';
import { Hub } from '../../places/hub/hub';
import { RouteLegWageType } from '../../route/route-leg-wage-type.enum';
import { DigitalLogType } from '../../shared/digital-log-type.enum';
import {
  WageEmployeeFilterType,
  WageEmployeeFilterTypeMapping
} from '../../shared/payroll/wage-employee-filter-type.enum';
import { DivisionLegWageSet } from './division-leg-wage-set';

//Should be off of RouteLegWageBase but linker is failing
//export class DivisionLegWageSetRule extends RouteLegWageBase implements SBaseDisplay {
export class DivisionLegWageSetRule extends EboObjectTimePeriod implements SBaseDisplay {
  setID: string;

  //Rule Set Instead
  ruleSetId: string;
  ruleSet: DivisionLegWageSet;

  //Filters
  employeeFilterType: WageEmployeeFilterType;
  logType: DigitalLogType;
  payrollGroupId: string;
  payrollGroup: EmployeePayrollGroup;
  employeeId: string;
  employee: Employee;
  companyId: string;
  company: Company;
  ruleOrder: number;
  days: DaysOfWeek;

  originHubID: number;
  originHub: Hub;
  destHubID: number;
  destHub: Hub;
  finalHubId: number;
  finalHub: Hub;
  wageType: RouteLegWageType;

  distance: number;
  payType: PayType;
  incrementalToWage: boolean;
  blockEmployeeWage: boolean;
  blockEmployeeLegPay: boolean;
  directOnly: boolean;
  amount: number;
  percentageOfGross: number;
  units: PayUnit;
  mileageAdjustment: number;
  comment: string;

  constructor(jsonData?: Partial<DivisionLegWageSetRule>) {
    super(jsonData);
    //console.log('jsonData', jsonData);
    if (jsonData) {
      if (jsonData.ruleSet) this.ruleSet = new DivisionLegWageSet(jsonData.ruleSet);
      if (jsonData.originHub) this.originHub = new Hub(jsonData.originHub);
      if (jsonData.destHub) this.destHub = new Hub(jsonData.destHub);
      if (jsonData.finalHub) this.finalHub = new Hub(jsonData.finalHub);
      if (jsonData.employee) this.employee = new Employee(jsonData.employee);
      if (jsonData.company) this.company = new Company(jsonData.company);
      if (jsonData.payrollGroup) this.payrollGroup = new EmployeePayrollGroup(jsonData.payrollGroup);
      if (isString(jsonData.wageType)) this.wageType = Number(jsonData.wageType);
    }
    this.directOnly ??= true;
    this.incrementalToWage ??= false;
    this.units ??= PayUnit.Dollars;
    this.payType ??= PayType.Solo;
    this.wageType ??= RouteLegWageType.FromTo;
    this.days ??= DaysOfWeek.AllDays;
    this.employeeFilterType ??= WageEmployeeFilterType.All;
    this.blockEmployeeWage ??= false;
    this.logType ??= DigitalLogType.FedExLinehaul;
    this.id ??= uuid();
  }

  get defaultSort() {
    const response = new Array<string>();
    if (SDate.isDayjs(this.endTime)) {
      response.push(this.endTime?.format('YYYYMMDD'));
    } else {
      response.push('--------');
    }
    response.push(NumberFormatter(this.ruleOrder, 4));
    response.push(this.employee ? this.employee.name : '~');
    response.push(this.payrollGroup ? this.payrollGroup.name : '~');
    response.push(this.company ? this.company.name : '~');
    response.push(this.startTime?.format('YYYYMMDD'));
    //console.log(`Processed -> result`, this, response)
    return response.join('-');
  }

  get amountSummary(): SummitFormattedText {
    if (this.ruleSetId) return null;
    const response = new SummitFormattedText();
    if (this.incrementalToWage) response.AddLine('Additional');
    if (this.units == PayUnit.Dollars) response.AddLine(MoneyFormatter(this.amount));
    if (this.units == PayUnit.Miles) response.AddLine(`${MileageFormatter(this.amount)} Miles`);
    if (this.units == PayUnit.MileageRate) response.AddLine(`${RateFormatter(this.amount)} / Mile`);
    if ((this.percentageOfGross ?? 0) != 0)
      response.AddLine(`${PercentageFormatter(this.percentageOfGross / 100)} of Gross`);
    if (this.blockEmployeeWage === true) response.AddLine(`[Flat]`);
    if (this.blockEmployeeLegPay === true) response.AddLine(`[Blocks Leg Wage]`);
    return response.lines.length > 0 ? response : null;
  }

  get dayString(): string {
    if (this.ruleSetId) return null;
    return DaysOfWeekToString(this.days);
  }

  get ruleSummary(): string {
    if (this.ruleSet) return null;
    const lclType = isString(this.wageType) ? Number(this.wageType) : this.wageType;
    switch (lclType) {
      case RouteLegWageType.ShuttleDistance:
        return `${this.originHubID}<=>? for ${this.distance} Miles`;
      case RouteLegWageType.FromTo:
        return `${this.originHubID}=>${this.destHubID}`;
      case RouteLegWageType.ShuttleDestination:
        if (this.directOnly) return `${this.originHubID}<=>${this.destHubID}`;
        else return `${this.originHubID}=>?=>${this.destHubID}`;
      case RouteLegWageType.ShuttleDifDestination:
        return `${this.originHubID}=>${this.destHubID}=>${this.finalHubId}`;
      case RouteLegWageType.MilesLessEqual:
        return `Distance <= ${this.distance} Miles`;
      default:
        return null;
    }
  }

  get applicableEmployee(): SummitFormattedText {
    const response = new SummitFormattedText();
    if (this.ruleSetId?.length > 0)
      response.AddLine(`[Rule Set]  ${this.ruleSet?.name}`, null, {
        path: ['/', 'divisions', 'legwages', this.ruleSetId]
      });
    if (!this.ruleSetId) {
      if (this.employeeFilterType == WageEmployeeFilterType.Employee && this.employeeId)
        response.AddLine(this.employee?.name);
      if (this.employeeFilterType == WageEmployeeFilterType.ByGroup && this.payrollGroupId)
        response.AddLine(this.payrollGroup?.name);
      if (this.employeeFilterType == WageEmployeeFilterType.ByEmployeeCompany && this.companyId)
        response.AddLine(this.company?.name);
    }
    if (response.lines.length == 0) response.AddLine(WageEmployeeFilterTypeMapping[WageEmployeeFilterType.All]);
    return response;
  }

  static sort(n1: DivisionLegWageSetRule, n2: DivisionLegWageSetRule) {
    return n1.defaultSort > n2.defaultSort ? 1 : -1;
  }

  toShortString(_param?: string): string {
    if (this.ruleSetId?.length > 0) return `[Rule Set] ${this.ruleSet?.name}`;
    let response = this.incrementalToWage ? 'Incremental ' : '';
    response += PayTypeMapping[this.payType] + ' ';
    switch (this.wageType) {
      case RouteLegWageType.ShuttleDistance:
        response += `${this.originHub?.toShortString()}<=>? for ${this.distance} Miles = ${MoneyFormatter(
          this.amount
        )}`;
        break;
      case RouteLegWageType.FromTo:
        response += `${this.originHub?.toShortString()}=>${this.destHub?.toShortString()} = ${MoneyFormatter(
          this.amount
        )}`;
        break;
      case RouteLegWageType.ShuttleDestination:
        if (this.directOnly)
          response += `${this.originHub?.toShortString()}<=>${this.destHub?.toShortString()} = ${MoneyFormatter(
            this.amount
          )}`;
        else
          response += `${this.originHub?.toShortString()}=>?=>${this.destHub?.toShortString()} = ${MoneyFormatter(
            this.amount
          )}`;
        break;
      case RouteLegWageType.ShuttleDifDestination:
        response += `${this.originHub?.toShortString()}=>${this.destHub?.toShortString()}=>${this.finalHub?.toShortString()} = ${MoneyFormatter(
          this.amount
        )}`;
        break;
      case RouteLegWageType.MilesLessEqual:
        response += `Distance <= ${this.distance} Miles = ${MoneyFormatter(this.amount)}`;
        break;
      default:
        break;
    }
    if (this.percentageOfGross) response += ` + ${PercentageFormatter(this.percentageOfGross / 100)} of Gross`;
    if (response.length == 0) return 'Unknown';
    return response;
  }

  HasDay(flag: DaysOfWeek): boolean {
    // eslint-disable-next-line no-bitwise
    return flag === (this.days & flag);
  }

  SetDay(flag: DaysOfWeek, value: boolean) {
    if (value) {
      // eslint-disable-next-line no-bitwise
      this.days |= flag;
    } else {
      // eslint-disable-next-line no-bitwise
      this.days &= ~flag;
    }
  }
}
